import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { careersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Briefcase, UploadCloud, FileText, LogOut, Loader2, CheckCircle2, Clock, MapPin, GraduationCap, Award } from 'lucide-react';

const errMsg = (e, d) => e?.response?.data?.detail || d;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_calibration-command/artifacts/0iqwnjwv_bhpl_logo.png';
const HERO_URL = 'https://static.prod-images.emergentagent.com/jobs/cfab0c57-a462-4909-bda4-cc3e2348e5fa/images/c62279862fe23fb76cd52f72665b54c46c6ee4df8b64c5d6df9af8bd9728a5c7.jpeg';
const STATUS_META = {
  shortlisted: { label: 'Shortlisted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Not selected', cls: 'bg-red-50 text-red-600 border-red-200' },
  applied: { label: 'Under review', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function CareersPage() {
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // auth dialog
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);

  // resume
  const [resumeBusy, setResumeBusy] = useState(false);

  // test dialog
  const [test, setTest] = useState(null); // {application_id, job_title, questions, question_ids, time_limit_min}
  const [answers, setAnswers] = useState({});
  const answersRef = useRef({}); // mirrors `answers` so timeout auto-submit sees latest selections
  const [timeLeft, setTimeLeft] = useState(0);
  const [testBusy, setTestBusy] = useState(false);

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem('careers_token')) { setCandidate(null); return; }
    try {
      const r = await careersAPI.me();
      setCandidate(r.data);
      const a = await careersAPI.applications();
      setApps(a.data || []);
    } catch {
      localStorage.removeItem('careers_token');
      setCandidate(null);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try { const r = await careersAPI.listJobs(); setJobs(r.data || []); } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadJobs(); loadMe(); }, [loadJobs, loadMe]);

  // Test timer
  useEffect(() => {
    if (!test || !timeLeft) return;
    const id = setInterval(() => setTimeLeft((t) => {
      if (t <= 1) { clearInterval(id); submitTest(true); return 0; }
      return t - 1;
    }), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [test]);

  const sendCode = async () => {
    setBusy(true);
    try {
      if (mode === 'register') {
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) { toast.error('Fill all fields'); setBusy(false); return; }
        await careersAPI.register(form);
      } else {
        if (!form.email.trim()) { toast.error('Enter your email'); setBusy(false); return; }
        await careersAPI.login(form.email);
      }
      setStep('otp');
      toast.success('Verification code sent to your email');
    } catch (e) { toast.error(errMsg(e, 'Could not send code')); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await careersAPI.verifyOtp(form.email, otp);
      localStorage.setItem('careers_token', r.data.token);
      setAuthOpen(false); setStep('form'); setOtp('');
      toast.success('Signed in');
      await loadMe();
    } catch (e) { toast.error(errMsg(e, 'Invalid code')); }
    finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem('careers_token'); setCandidate(null); setApps([]); toast.success('Signed out'); };

  const uploadResume = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) { toast.error('Resume must be a PDF'); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error('Resume too large (max 2 MB)'); return; }
    setResumeBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      await careersAPI.uploadResume(fd);
      toast.success('Resume uploaded');
      await loadMe();
    } catch (er) { toast.error(errMsg(er, 'Upload failed')); }
    finally { setResumeBusy(false); }
  };

  const appliedJobIds = new Set(apps.map((a) => a.job_id));

  const apply = async (job) => {
    if (!candidate) { setAuthOpen(true); return; }
    if (!candidate.has_resume) { toast.error('Please upload your resume first'); return; }
    try {
      await careersAPI.apply(job.id);
      toast.success(`Applied for ${job.title}`);
      await loadMe();
    } catch (e) { toast.error(errMsg(e, 'Could not apply')); }
  };

  const startTest = async (application_id) => {
    try {
      const r = await careersAPI.getTest(application_id);
      setTest(r.data); setAnswers({}); answersRef.current = {};
      setTimeLeft((r.data.time_limit_min || 0) * 60);
    } catch (e) { toast.error(errMsg(e, 'Could not load test')); }
  };

  const submitTest = async (auto = false) => {
    if (!test) return;
    setTestBusy(true);
    try {
      const current = answersRef.current;
      const payload = test.questions.map((q) => ({ question_id: q.id, selected_index: current[q.id] ?? -1 }));
      const r = await careersAPI.submitTest(test.application_id, test.question_ids, payload);
      const answered = payload.filter((p) => p.selected_index >= 0).length;
      toast.success(auto
        ? `Time up — auto-submitted ${answered}/${payload.length} answered (score ${r.data.score}/${r.data.total})`
        : `Test submitted (${r.data.score}/${r.data.total})`);
      setTest(null); setTimeLeft(0);
      await loadMe();
    } catch (e) { toast.error(errMsg(e, 'Could not submit')); setTest(null); }
    finally { setTestBusy(false); }
  };

  const openJD = async (jid) => {
    try {
      const r = await careersAPI.downloadJD(jid);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('No job description available'); }
  };

  const mmss = `${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="careers-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO_URL} alt="BHPL" className="w-9 h-9 sm:w-10 sm:h-10 object-contain bg-white/95 rounded-md p-1 shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold truncate">BHPL Careers</h1>
              <p className="text-[11px] text-slate-400 hidden sm:block">Build your future with us</p>
            </div>
          </div>
          {candidate ? (
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <span className="text-sm hidden sm:block max-w-[140px] truncate">{candidate.name}</span>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 px-2 sm:px-3" onClick={logout} data-testid="careers-logout"><LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Sign out</span></Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="secondary" onClick={() => { setMode('login'); setStep('form'); setAuthOpen(true); }} data-testid="careers-login-btn">Log in</Button>
              <Button size="sm" className="bg-pink-600 hover:bg-pink-700" onClick={() => { setMode('register'); setStep('form'); setAuthOpen(true); }} data-testid="careers-register-btn">Register</Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero banner */}
      <section className="relative overflow-hidden bg-[#0F172A]" data-testid="careers-hero">
        <img src={HERO_URL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 sm:opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-[#0F172A]/40 sm:to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 py-12 sm:py-20">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-pink-400 font-semibold mb-3">
              <Briefcase className="w-3.5 h-3.5" /> We're hiring
            </span>
            <h2 className="text-[28px] leading-tight sm:text-4xl font-bold text-white">
              Precision work.<br />Real careers.
            </h2>
            <p className="text-slate-300 text-sm mt-3">
              Join BHPL's engineering and manufacturing teams. Browse open roles below, apply in minutes, and take a short assessment — no account passwords, just your email.
            </p>
            {!candidate && (
              <Button className="mt-5 w-full sm:w-auto bg-pink-600 hover:bg-pink-700" onClick={() => { setMode('register'); setStep('form'); setAuthOpen(true); }} data-testid="hero-cta-btn">
                Create your candidate profile
              </Button>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Candidate profile / resume */}
        {candidate && (
          <Card data-testid="candidate-profile-card">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Your Profile</CardTitle></CardHeader>
            <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-medium break-words">{candidate.name} · {candidate.email}</div>
                <div className="text-slate-500 text-xs">Phone: {candidate.phone}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {candidate.has_resume
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 max-w-full"><CheckCircle2 className="w-3 h-3 mr-1 shrink-0" /><span className="truncate">{candidate.resume_filename || 'Resume uploaded'}</span></Badge>
                  : <Badge className="bg-amber-50 text-amber-700 border-amber-200">No resume yet</Badge>}
                <label className="inline-flex">
                  <input type="file" accept="application/pdf" className="hidden" onChange={uploadResume} data-testid="resume-file-input" />
                  <span className={`inline-flex items-center text-xs px-3 py-1.5 rounded-md cursor-pointer ${resumeBusy ? 'bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`} data-testid="resume-upload-label">
                    {resumeBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UploadCloud className="w-3 h-3 mr-1" />}
                    {candidate.has_resume ? 'Replace resume (PDF ≤2MB)' : 'Upload resume (PDF ≤2MB)'}
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My applications */}
        {candidate && apps.length > 0 && (
          <Card data-testid="my-applications-card">
            <CardHeader className="pb-3"><CardTitle className="text-base">My Applications</CardTitle></CardHeader>
            <CardContent className="divide-y">
              {apps.map((a) => (
                <div key={a.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`application-${a.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium break-words">{a.job_title}</div>
                    <div className="text-[11px] text-slate-500">Applied {(a.applied_at || '').slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={`text-[10px] ${(STATUS_META[a.status] || STATUS_META.applied).cls}`} data-testid={`app-status-${a.id}`}>
                      {(STATUS_META[a.status] || STATUS_META.applied).label}
                    </Badge>
                    {a.test_taken
                      ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Test · {a.test_score}/{a.test_total}</Badge>
                      : <Button size="sm" onClick={() => startTest(a.id)} data-testid={`take-test-${a.id}`}>Take test</Button>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Open positions */}
        <div>
          <h2 className="text-base font-semibold text-[#0F172A] mb-3">Open Positions</h2>
          {loading ? <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
            : jobs.length === 0 ? <div className="text-sm text-slate-400">No open positions right now. Please check back later.</div>
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="jobs-grid">
                {jobs.map((j) => (
                  <Card key={j.id} data-testid={`job-card-${j.id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">{j.title}</CardTitle>
                      <div className="flex flex-wrap gap-2 text-[11px] text-slate-500 mt-1">
                        {j.department && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{j.department}</span>}
                        {j.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>}
                        {j.has_test && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{j.time_limit_min}min test</span>}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {j.position_description && <p className="text-slate-600 line-clamp-3">{j.position_description}</p>}
                      {j.experience_criteria && <p className="text-xs text-slate-500 flex items-start gap-1"><Award className="w-3 h-3 mt-0.5" />{j.experience_criteria}</p>}
                      {j.education_criteria && <p className="text-xs text-slate-500 flex items-start gap-1"><GraduationCap className="w-3 h-3 mt-0.5" />{j.education_criteria}</p>}
                      <div className="flex items-center gap-2 pt-1">
                        {j.has_jd && <Button size="sm" variant="outline" onClick={() => openJD(j.id)} data-testid={`jd-${j.id}`}>Job description</Button>}
                        {appliedJobIds.has(j.id)
                          ? <Badge className="bg-slate-100 text-slate-600 border-slate-200">Applied</Badge>
                          : <Button size="sm" className="bg-pink-600 hover:bg-pink-700" onClick={() => apply(j)} data-testid={`apply-${j.id}`}>Apply</Button>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
        </div>
        <div className="text-center pt-4">
          <button className="text-xs text-slate-400 hover:text-slate-600 underline" onClick={() => navigate('/login')} data-testid="careers-staff-login">Staff? Go to ERP login</button>
        </div>
      </main>

      {/* Auth dialog */}
      <Dialog open={authOpen} onOpenChange={(o) => { setAuthOpen(o); if (!o) { setStep('form'); setOtp(''); } }}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-lg" data-testid="careers-auth-dialog">
          <DialogHeader>
            <DialogTitle>{mode === 'register' ? 'Create a candidate account' : 'Candidate log in'}</DialogTitle>
            <DialogDescription className="text-xs">
              {step === 'form' ? 'We verify your email with a one-time code. No password needed.' : `Enter the 6-digit code sent to ${form.email}.`}
            </DialogDescription>
          </DialogHeader>
          {step === 'form' ? (
            <div className="space-y-3">
              {mode === 'register' && (
                <div><Label className="text-xs">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" /></div>
              )}
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" /></div>
              {mode === 'register' && (
                <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" /></div>
              )}
              <div className="text-center text-xs text-slate-500">
                {mode === 'register'
                  ? <>Already registered? <button className="text-pink-600 underline" onClick={() => setMode('login')}>Log in</button></>
                  : <>New here? <button className="text-pink-600 underline" onClick={() => setMode('register')}>Register</button></>}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label className="text-xs">Verification code</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} data-testid="otp-input" /></div>
              <button className="text-xs text-pink-600 underline" onClick={async () => { try { await careersAPI.resendOtp(form.email); toast.success('New code sent'); } catch (e) { toast.error(errMsg(e, 'Could not resend')); } }}>Resend code</button>
            </div>
          )}
          <DialogFooter>
            {step === 'form'
              ? <Button onClick={sendCode} disabled={busy} data-testid="send-code-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send code'}</Button>
              : <Button onClick={verify} disabled={busy} data-testid="verify-otp-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & continue'}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test dialog */}
      <Dialog open={!!test} onOpenChange={(o) => { if (!o && test) { /* prevent accidental close */ } }}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg" data-testid="test-dialog">
          <DialogHeader>
            <DialogTitle className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-base sm:text-lg">{test?.job_title} — Assessment</span>
              {timeLeft > 0 && <span className="text-sm font-mono text-pink-600" data-testid="test-timer"><Clock className="w-4 h-4 inline mr-1" />{mmss}</span>}
            </DialogTitle>
            <DialogDescription className="text-xs">Answer all questions. You can take this test only once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {test?.questions.map((q, i) => (
              <div key={q.id} data-testid={`q-${q.id}`}>
                <div className="text-sm font-medium mb-2">{i + 1}. {q.text}</div>
                <RadioGroup value={answers[q.id]?.toString() ?? ''} onValueChange={(v) => { const next = { ...answersRef.current, [q.id]: parseInt(v, 10) }; answersRef.current = next; setAnswers(next); }}>
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2 py-1">
                      <RadioGroupItem value={oi.toString()} id={`${q.id}-${oi}`} data-testid={`opt-${q.id}-${oi}`} />
                      <Label htmlFor={`${q.id}-${oi}`} className="text-sm font-normal cursor-pointer leading-snug">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => submitTest(false)} disabled={testBusy} className="w-full sm:w-auto bg-pink-600 hover:bg-pink-700" data-testid="submit-test-btn">
              {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
